import { Outlet } from 'react-router-dom'
import { useLocation } from 'react-router-dom'
import { m, LazyMotion, domAnimation } from 'framer-motion'
import { Suspense, useMemo, useState } from 'react'

import { useTenant } from '@/lib/provider/TenantProvider'
import { useApp } from '@/lib/provider/AppProvider'
import FeedbackIcon from '@icons/chat-bubble-left-right'
import FeedbackPanel from '@/components/FeedbackPanel'

import Navigation from '@/layouts/partials/Navigation'
import CustomerBanner from '@/components/CustomerBanner'
import TenantNavigation from '@/layouts/partials/TenantNavigation'
import Footer from '@/layouts/partials/Footer'
import AnalyticsBox from '@/components/AnalyticsBox'
import EmailVerificationBanner from '@/components/EmailVerificationBanner'
import { useAuth } from '@/lib/provider/AuthProvider'
import XMarkIcon from '@icons/x-mark'
import { cn } from '@/lib/misc/Utils'
import { FLAGS, useFeatureFlag } from '@/lib/hooks/useFeatureFlag'
import TwoFactorEnforcementDialog from '@/components/TwoFactorEnforcementDialog'
import { createPortal } from 'react-dom'
import AddService from '@/pages/dashboard/partials/AddService'

const pageVariants = {
  initial: {
    opacity: 0,
    scale: 0.99,
  },
  in: {
    opacity: 1,
    scale: 1,
  },
  out: {
    opacity: 0,
    scale: 0.9,
  },
}

const pageTransition = {
  type: 'tween' as const,
  ease: 'anticipate' as const,
  duration: 0.5,
}

const MemberLayout = () => {
  const [appState] = useApp()
  const location = useLocation()
  const { customerId } = useTenant()
  const [authState] = useAuth()
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false)

  const isFeedbackEnabled = useFeatureFlag(FLAGS.FEEDBACK)
  const isUserAnalyticsEnabled = useFeatureFlag(FLAGS.USER_ANALYTICS)

  const shouldShowAnalyticsBox = useMemo(
    () =>
      authState.user &&
      appState.appInfo?.analytics === null &&
      isUserAnalyticsEnabled,
    [appState.appInfo, isUserAnalyticsEnabled, authState.user]
  )

  const isTicketsPage = useMemo(
    () => location.pathname.startsWith('/tickets'),
    [location.pathname]
  )

  const shouldShowTfaEnforcementBanner = useMemo(
    () => authState.user && appState.appInfo?.enforce_tfa === true,
    [authState.user, appState.appInfo]
  )

  const tfaEnforcementAllowSkip = useMemo(
    () => appState.appInfo?.tfa_enforcement_allow_skip === true,
    [appState.appInfo]
  )

  const tfaEnforcementType = useMemo(
    () => appState.appInfo?.tfa_enforcement_type ?? 'none',
    [appState.appInfo]
  )

  const animateKey = useMemo(() => {
    return isTicketsPage ? 'tickets' : location.pathname
  }, [isTicketsPage, location.pathname])

  if (shouldShowTfaEnforcementBanner) {
    return (
      <div className="flex flex-col min-h-full w-full bg-radial from-accent-60 to-accent-90">
        <TwoFactorEnforcementDialog
          isOpen
          allowSkip={tfaEnforcementAllowSkip}
          tfaEnforcementType={tfaEnforcementType}
        />
      </div>
    )
  }

  return (
    <LazyMotion features={domAnimation} strict>
      <div
        className={cn(
          'flex flex-col min-h-full w-full',
          shouldShowTfaEnforcementBanner
            ? 'bg-white'
            : 'bg-gradient-to-br from-brand-1 to-[#1a0630]'
        )}
        id="member-layout"
        data-cy="member-layout"
      >
        {customerId ? (
          <>
            <CustomerBanner />
            <TenantNavigation />
          </>
        ) : (
          <div>
            <Navigation />
          </div>
        )}

        <div className="flex-1 flex bg-neutral-10 rounded-xl mx-2 shadow-[0px_0px_20px_0px_rgba(0,0,0,0.6)]">
          <main
            className={cn(
              'flex-1 overflow-x-hidden',
              isTicketsPage ? '' : 'min-h-screen'
            )}
          >
            {appState.appInfo &&
              typeof appState.appInfo.email_verified === 'boolean' &&
              appState.appInfo.email_verified === false && (
                <EmailVerificationBanner />
              )}

            <m.div
              key={animateKey}
              initial="initial"
              animate="in"
              exit="out"
              variants={pageVariants}
              transition={pageTransition}
            >
              <Outlet />
            </m.div>
          </main>
        </div>

        {shouldShowAnalyticsBox && (
          <m.div variants={pageVariants}>
            <AnalyticsBox />
          </m.div>
        )}

        {createPortal(
          <Suspense>
            <AddService />
          </Suspense>,
          document.body
        )}

        <Footer className="px-6" />

        {isFeedbackEnabled && (
          <>
            <button
              className={cn(
                'fixed right-3 bottom-2 z-50 flex size-12 rounded-full items-center justify-center bg-brand-5 shadow-lg transition-colors hover:bg-brand-1 hover:scale-105 transition-transform sm:right-3 right-0',
                isFeedbackOpen
                  ? 'sm:-translate-x-40 -translate-x-[43vw]'
                  : 'translate-x-0'
              )}
              onClick={() => setIsFeedbackOpen(!isFeedbackOpen)}
            >
              {isFeedbackOpen ? (
                <XMarkIcon className="size-6 text-white" />
              ) : (
                <FeedbackIcon className="size-6 text-white" />
              )}
            </button>
            <FeedbackPanel
              isOpen={isFeedbackOpen}
              onClose={() => setIsFeedbackOpen(false)}
            />
          </>
        )}
      </div>
    </LazyMotion>
  )
}
export default MemberLayout
